/**
 * One Build on the wire (step 8.2 of docs/plans/onboarding-harness/phase-08.md):
 * `POST /v1/setup/sessions/:id/build { version }` makes the workspace from the
 * stored snapshot of that version, in one transaction, keyed by the session
 * and the version so a retry, a reload or a second device find the one
 * result. Its steps, its result and its refusals are said here; the session's
 * events (`build_step`, `built`, `build_refused`, `build_failed`, `import`)
 * carry the same shapes.
 *
 * Pure: the phone bundles this package, and reads none of it.
 */
import type { StudioOp } from './setup-draft/ops';
import type { StudioPartRef } from './setup-draft/paths';

/** The steps of a build, in the order the server makes them. */
export const SETUP_BUILD_STEPS = ['records', 'lists', 'cards', 'links', 'roles'] as const;
export type SetupBuildStepName = (typeof SETUP_BUILD_STEPS)[number];

export type SetupBuildRequest = {
  readonly version: number;
  /**
   * The business details on the Build sheet the person kept (D14 of
   * docs/plans/report-templates.md): candidate ids from
   * `GET …/details`. Absent or empty keeps none; Build is their confirmation.
   */
  readonly keepDetails?: readonly string[];
};

/** A build keeps at most this many details (`setup_session.details` holds 40). */
export const SETUP_KEEP_DETAILS_MAX = 40;

/** One step as it lands: how much of it is made, and the canvas keys made so far in it. */
export type SetupBuildProgress = {
  readonly step: SetupBuildStepName;
  readonly done: number;
  readonly total: number;
  readonly keys: readonly string[];
};

/**
 * A sheet the draft maps into a record or list (8.3; F2 of
 * docs/plans/setup-file-data.md). A list's sheet with no rows of its own
 * starts right after the build (`starting`). A record's rows never start at
 * Build: they wait for the person's review (`pending`), who brings them in
 * (`started`, with the job), or skips them (`skipped`).
 */
export type SetupBuildImport = {
  readonly fileId: string;
  readonly sheet: string;
  readonly recordKey: string;
  readonly name: string;
  readonly status: SetupBuildImportStatus;
  readonly jobId?: string;
  readonly reason?: string;
  /** When a retry began starting it again; a retry waits on one younger than `IMPORT_RETRY_AFTER_MS`. */
  readonly startedAt?: string;
};

export const SETUP_BUILD_IMPORT_STATUSES = ['pending', 'starting', 'started', 'failed', 'skipped'] as const;
export type SetupBuildImportStatus = (typeof SETUP_BUILD_IMPORT_STATUSES)[number];

/** An import the person has not decided yet: it waits for the review step. */
export const isReviewPending = (one: Pick<SetupBuildImport, 'status'>): boolean => one.status === 'pending';

/** A `starting` import older than this (from its `startedAt`, else the build's `builtAt`) may be started again. */
export const IMPORT_RETRY_AFTER_MS = 120_000;

export type SetupBuildResult = {
  readonly tenantId: string;
  readonly name: string;
  readonly builtAt: string;
  readonly ids: { readonly records: Record<string, string>; readonly roles: Record<string, string> };
  readonly counts: {
    readonly records: number;
    readonly lists: number;
    readonly links: number;
    readonly cards: number;
    readonly roles: number;
    readonly listRows: number;
  };
  readonly imports: readonly SetupBuildImport[];
  /**
   * When the person opened the workspace from the studio (`POST …/opened`).
   * Until then the built session is the one `GET /v1/setup/sessions` answers
   * as open, so the built panel stays wherever they come back.
   */
  readonly openedAt?: string;
  /**
   * Bring in your data (F2, F6 of docs/plans/setup-file-data.md): `open` once
   * Build or a yes to the one-time offer opened the review step; `declined`
   * once the person said no to the offer, never asked again. Absent on a
   * workspace built before the review step, until it is answered.
   */
  readonly review?: 'open' | 'declined';
};

/** A fix OS offers for a refusal: one line, and the strict ops it would commit. The server never commits them. */
export type SetupBuildFix = { readonly label: string; readonly ops: readonly StudioOp[] };

/**
 * One piece Build could not make (16 Oct, Sharma Dairy: Build refused, OS
 * found no fix, and the person was left with Create off and nothing to
 * press): the part, its name, why, in the person's words, and the ops that
 * leave it out of the setup, so Leave it out and build is one press. Empty
 * `leaveOut` means the piece cannot simply go (a record, a field), and only
 * OS can change it.
 */
export type SetupBuildPiece = {
  readonly part: string;
  readonly name: string;
  readonly reasons: readonly string[];
  readonly leaveOut: readonly StudioOp[];
};

export type SetupBuildRefusal =
  | { readonly code: 'NAME_TAKEN'; readonly name: string; readonly tenantId: string | null; readonly on: readonly string[]; readonly fix: null }
  | {
      readonly code: 'LINK_INVALID' | 'ROLE_INVALID' | 'SETUP_INVALID';
      readonly message: string;
      readonly on: readonly string[];
      readonly fix: SetupBuildFix | null;
      /** What could not be made, each with its reasons and the way to leave it out. Never empty from this server; absent from an older one. */
      readonly pieces: readonly SetupBuildPiece[];
    };

/**
 * The ops that leave one part out of a setup: a link, a role, a card, a
 * Dashboard card or an import goes on its own. A record or a field has
 * others leaning on it, so none: OS changes it, or the person does.
 */
export const leaveOutOpsOf = (ref: StudioPartRef | null): StudioOp[] => {
  if (ref === null) return [];
  switch (ref.kind) {
    case 'link':
      return [{ op: 'remove_link', link: ref.key }];
    case 'role':
      return [{ op: 'remove_role', role: ref.key }];
    case 'card':
      return [{ op: 'remove_card', record: ref.record, card: ref.card }];
    case 'board':
      return [{ op: 'remove_board_card', card: ref.key }];
    case 'import':
      return [{ op: 'remove_import', import: ref.key }];
    default:
      return [];
  }
};

export type SetupBuildResponse = { readonly status: 'built'; readonly result: SetupBuildResult };

/** A refusal as the route sends it: the usual error, and the refusal beside it. */
export type SetupBuildRefusalBody = { readonly error: { readonly code: string; readonly message: string }; readonly refusal: SetupBuildRefusal };

/** How long a build holds its session before another may take it over (a server that died mid-build). */
export const BUILD_LEASE_MS = 60_000;

/* ---------------------------------------------------------------- readers */

/** The ceilings the readers hold a body to. */
const BOUND = { text: 2000, keys: 200, ids: 400, imports: 40, ops: 200, pieces: 100, reasons: 20 } as const;

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;
const isText = (value: unknown, max: number = BOUND.text): value is string => typeof value === 'string' && value.length <= max;
const isTexts = (value: unknown, max: number): value is string[] => Array.isArray(value) && value.length <= max && value.every((one) => isText(one));

const readIdMap = (value: unknown): Record<string, string> | null => {
  if (!isObject(value)) return null;
  const entries = Object.entries(value);
  if (entries.length > BOUND.ids || !entries.every(([, id]) => isText(id, 64))) return null;
  return Object.fromEntries(entries) as Record<string, string>;
};

const IMPORT_STATUSES: readonly string[] = SETUP_BUILD_IMPORT_STATUSES;

const readImport = (raw: unknown): SetupBuildImport | null => {
  if (!isObject(raw)) return null;
  const { fileId, sheet, recordKey, name, status, jobId, reason, startedAt } = raw;
  if (!isText(fileId, 64) || !isText(sheet, 200) || !isText(recordKey, 64) || !isText(name, 300)) return null;
  if (typeof status !== 'string' || !IMPORT_STATUSES.includes(status)) return null;
  if (jobId !== undefined && !isText(jobId, 64)) return null;
  if (reason !== undefined && !isText(reason)) return null;
  if (startedAt !== undefined && !isText(startedAt, 64)) return null;
  return {
    fileId,
    sheet,
    recordKey,
    name,
    status: status as SetupBuildImport['status'],
    ...(jobId === undefined ? {} : { jobId }),
    ...(reason === undefined ? {} : { reason }),
    ...(startedAt === undefined ? {} : { startedAt }),
  };
};

/** One import as an `import` event carries it, or null. */
export const readSetupBuildImport = readImport;

const COUNTS = ['records', 'lists', 'links', 'cards', 'roles', 'listRows'] as const;

/** A build's result as `build_result` and the `built` event carry it, or null. */
export const readSetupBuildResult = (raw: unknown): SetupBuildResult | null => {
  if (!isObject(raw)) return null;
  const { tenantId, name, builtAt, ids, counts, imports, openedAt, review } = raw;
  if (!isText(tenantId, 64) || !isText(name, 200) || !isText(builtAt, 64)) return null;
  if (openedAt !== undefined && !isText(openedAt, 64)) return null;
  if (review !== undefined && review !== 'open' && review !== 'declined') return null;
  if (!isObject(ids) || !isObject(counts) || !Array.isArray(imports) || imports.length > BOUND.imports) return null;
  const records = readIdMap(ids.records);
  const roles = readIdMap(ids.roles);
  if (records === null || roles === null) return null;
  if (!COUNTS.every((key) => isCount(counts[key]))) return null;
  const read = imports.map(readImport);
  if (read.some((one) => one === null)) return null;
  return {
    tenantId,
    name,
    builtAt,
    ids: { records, roles },
    counts: Object.fromEntries(COUNTS.map((key) => [key, counts[key]])) as SetupBuildResult['counts'],
    imports: read as SetupBuildImport[],
    ...(openedAt === undefined ? {} : { openedAt: openedAt as string }),
    ...(review === undefined ? {} : { review: review as 'open' | 'declined' }),
  };
};

/** `201`/`200` of the build route, or null. */
export const readSetupBuildResponse = (raw: unknown): SetupBuildResponse | null => {
  if (!isObject(raw) || raw.status !== 'built') return null;
  const result = readSetupBuildResult(raw.result);
  return result === null ? null : { status: 'built', result };
};

const FIXABLE: readonly string[] = ['LINK_INVALID', 'ROLE_INVALID', 'SETUP_INVALID'];

/** A refusal, bare or as the route sends it (`{ error, refusal }`), or null. */
export const readSetupBuildRefusal = (raw: unknown): SetupBuildRefusal | null => {
  const body = isObject(raw) && isObject(raw.refusal) ? raw.refusal : raw;
  if (!isObject(body) || !isTexts(body.on, BOUND.keys)) return null;
  const on = [...body.on];
  if (body.code === 'NAME_TAKEN') {
    if (!isText(body.name, 200) || !(body.tenantId === null || isText(body.tenantId, 64)) || body.fix !== null) return null;
    return { code: 'NAME_TAKEN', name: body.name, tenantId: body.tenantId, on, fix: null };
  }
  if (typeof body.code !== 'string' || !FIXABLE.includes(body.code) || !isText(body.message)) return null;
  let fix: SetupBuildFix | null = null;
  if (body.fix !== null) {
    if (!isObject(body.fix) || !isText(body.fix.label) || !Array.isArray(body.fix.ops) || body.fix.ops.length > BOUND.ops) return null;
    fix = { label: body.fix.label, ops: [...body.fix.ops] as StudioOp[] };
  }
  // An older server sent none: the refusal still reads, with no piece to leave out.
  let pieces: SetupBuildPiece[] = [];
  if (body.pieces !== undefined) {
    if (!Array.isArray(body.pieces) || body.pieces.length > BOUND.pieces) return null;
    const read = body.pieces.map(readPiece);
    if (read.some((one) => one === null)) return null;
    pieces = read as SetupBuildPiece[];
  }
  return { code: body.code as 'LINK_INVALID' | 'ROLE_INVALID' | 'SETUP_INVALID', message: body.message, on, fix, pieces };
};

const readPiece = (raw: unknown): SetupBuildPiece | null => {
  if (!isObject(raw) || !isText(raw.part, 300) || !isText(raw.name, 300) || !isTexts(raw.reasons, BOUND.reasons)) return null;
  if (!Array.isArray(raw.leaveOut) || raw.leaveOut.length > BOUND.ops || !raw.leaveOut.every(isObject)) return null;
  return { part: raw.part, name: raw.name, reasons: [...raw.reasons], leaveOut: [...raw.leaveOut] as StudioOp[] };
};

/** A `build_step` event's progress, or null. */
export const readSetupBuildProgress = (raw: unknown): SetupBuildProgress | null => {
  if (!isObject(raw)) return null;
  const { step, done, total, keys } = raw;
  if (typeof step !== 'string' || !(SETUP_BUILD_STEPS as readonly string[]).includes(step)) return null;
  if (!isCount(done) || !isCount(total) || done > total || !isTexts(keys, BOUND.ids)) return null;
  return { step: step as SetupBuildStepName, done, total, keys: [...keys] };
};
